import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { 
  ArrowLeft,
  Bell,
  RefreshCw,
  AlertTriangle,
  FileText,
  CheckCircle,
  X,
  Upload
} from "lucide-react";
import { SBER, API, pageVariants, tapAnimation } from "../utils/constants";

const NotificationsPage = ({ user, onBack, onNavigate }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hasDocumentRequests, setHasDocumentRequests] = useState(false);

  const fetchNotifications = async () => {
    if (!user?.telegram_id) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/user/notifications?telegram_id=${user.telegram_id}`);
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
      // Check if there are pending document requests
      const hasRequests = res.data.notifications?.some(n => n.type === 'DOCUMENT_REQUEST' && !n.is_read);
      setHasDocumentRequests(hasRequests);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    }
    setLoading(false);
  };

  useEffect(() => { fetchNotifications(); }, [user]);

  const handleMarkRead = async (notificationId) => {
    try {
      await axios.put(`${API}/user/notifications/${notificationId}/read?telegram_id=${user.telegram_id}`);
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking notification read:', err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.put(`${API}/user/notifications/read-all?telegram_id=${user.telegram_id}`);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success('Все уведомления прочитаны');
    } catch (err) {
      toast.error('Ошибка');
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'KYC_COMMENT':
        return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      case 'DOCUMENT_REQUEST':
        return <FileText className="w-5 h-5 text-blue-500" />;
      case 'KYC_APPROVED':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'KYC_REJECTED':
        return <X className="w-5 h-5 text-red-500" />;
      default:
        return <Bell className="w-5 h-5" style={{ color: SBER.green }} />;
    }
  };

  const getNotificationBg = (type, isRead) => {
    if (isRead) return SBER.grayPale;
    switch (type) {
      case 'KYC_COMMENT':
        return '#FEF3C7';
      case 'DOCUMENT_REQUEST':
        return '#DBEAFE';
      case 'KYC_APPROVED':
        return '#D1FAE5';
      case 'KYC_REJECTED':
        return '#FEE2E2';
      default:
        return SBER.greenPale;
    }
  };

  return (
    <motion.div 
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="min-h-screen pb-24"
      style={{ background: SBER.bg }}
    >
      {/* Header */}
      <div 
        className="px-5 pt-6 pb-4 flex items-center gap-4"
        style={{ background: SBER.white }}
      >
        <motion.button whileTap={tapAnimation} onClick={onBack}>
          <ArrowLeft className="w-6 h-6" style={{ color: SBER.dark }} />
        </motion.button>
        <div className="flex-1">
          <h1 className="text-xl font-bold" style={{ color: SBER.dark }}>Уведомления</h1>
          {unreadCount > 0 && (
            <p className="text-xs" style={{ color: SBER.gray }}>
              {unreadCount} непрочитанных
            </p>
          )}
        </div>
        {notifications.length > 0 && unreadCount > 0 && (
          <motion.button
            whileTap={tapAnimation}
            onClick={handleMarkAllRead}
            className="text-xs px-3 py-1.5 rounded-lg"
            style={{ background: SBER.greenPale, color: SBER.green }}
          >
            Прочитать все
          </motion.button>
        )}
      </div>

      {/* Document Upload Button - Always show */}
      {onNavigate && (
        <div className="px-5 py-2">
          <motion.button
            whileTap={tapAnimation}
            onClick={() => onNavigate('upload-documents')}
            className="w-full py-3 rounded-2xl font-semibold text-white flex items-center justify-center gap-2"
            style={{ background: hasDocumentRequests ? '#EF4444' : '#3B82F6' }}
            data-testid="upload-docs-nav-btn"
          >
            <Upload className="w-5 h-5" />
            {hasDocumentRequests ? 'Загрузить запрошенные документы' : 'Загрузить документы'}
          </motion.button>
        </div>
      )}

      {/* Notifications List */}
      <div className="px-5 py-4 space-y-3">
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 mx-auto animate-spin" style={{ color: SBER.green }} />
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-12">
            <Bell className="w-12 h-12 mx-auto mb-3" style={{ color: SBER.grayLight }} />
            <p style={{ color: SBER.gray }}>Нет уведомлений</p>
          </div>
        ) : (
          notifications.map((notification) => (
            <motion.div
              key={notification.id}
              whileTap={tapAnimation}
              onClick={() => !notification.is_read && handleMarkRead(notification.id)}
              className="p-4 rounded-2xl cursor-pointer"
              style={{ 
                background: getNotificationBg(notification.type, notification.is_read),
                opacity: notification.is_read ? 0.7 : 1
              }}
            >
              <div className="flex items-start gap-3">
                <div className="mt-0.5">
                  {getNotificationIcon(notification.type)}
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-semibold text-sm" style={{ color: SBER.dark }}>
                      {notification.title}
                    </p>
                    {!notification.is_read && (
                      <span className="w-2 h-2 rounded-full" style={{ background: SBER.green }} />
                    )}
                  </div>
                  <p className="text-sm" style={{ color: SBER.gray }}>
                    {notification.message}
                  </p>
                  {notification.documents && notification.documents.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {notification.documents.map((doc, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-xs" style={{ color: SBER.dark }}>
                          <FileText className="w-3 h-3" />
                          {doc}
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-xs mt-2" style={{ color: SBER.grayLight }}>
                    {new Date(notification.created_at).toLocaleString('ru-RU')}
                  </p>
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>
    </motion.div>
  );
};

export default NotificationsPage;
